import { DollarSign, Percent, TrendingUpDown } from '@tamagui/lucide-icons';
import {
  DraggablePortfolioTableColumn,
  PortfolioTableColumn,
  TableColumn
} from '@unistocks/features/portfolios/components/portfolio-table/types';
import { getColumnSize } from '@unistocks/features/portfolios/components/portfolio-table/utils';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { isPortfolio as isPortfolioCheck } from '@unistocks/features/portfolios/portfolio-utils';
import { DateConstant } from '@unistocks/proxy/axios-client';
import { t } from '@unistocks/shared/localization/index.ts';
import { useMemo } from 'react';

export const DEFAULT_PORTFOLIO_COLUMNS: TableColumn[] = [
  'currentPriceChange',
  'totalChange',
  'calculatedValue',
  'calculatedValueShare'
];

export const DEFAULT_WATCHLIST_COLUMNS: TableColumn[] = [
  'ThisYear',
  'currentPriceChange',
  'currentPrice'
];

export const NON_SELECTABLE_COLUMNS: TableColumn[] = ['name', 'updated'];

const createTableColumns = (portfolio: PortfolioBase) => {
  const isPortfolio = isPortfolioCheck(portfolio);
  const currency = isPortfolio ? portfolio.currency : null;

  const columns: PortfolioTableColumn[] = [
    {
      prop: 'currentPriceChange',
      sortProp: 'PercentageChange',
      displayName: t(getTranslationKey('currentPriceChange')),
      size: getColumnSize('currentPriceChange'),
      Icon: TrendingUpDown
    }
  ];

  if (isPortfolio) {
    columns.push(
      {
        prop: 'totalChange',
        sortProp: 'TotalChangePercent',
        displayName: t(getTranslationKey('totalChange')),
        size: getColumnSize('totalChange'),
        Icon: Percent
      },
      {
        prop: 'calculatedValue',
        sortProp: 'CalculatedValue',
        displayName: t(getTranslationKey('calculatedValue')),
        size: getColumnSize('calculatedValue'),
        Icon: DollarSign
      },
      {
        prop: 'calculatedValueShare',
        displayName: t(getTranslationKey('calculatedValueShare')),
        size: getColumnSize('calculatedValueShare')
      },
      {
        prop: 'averageAcquisitionPrice',
        sortProp: 'CalculatedAverageAcquisitionPrice',
        displayName: `${t(getTranslationKey('averageAcquisitionPrice'))} (${currency})`,
        size: getColumnSize('averageAcquisitionPrice'),
        Icon: DollarSign
      },
      {
        prop: 'averageAcquisitionPriceAssetCurrency',
        displayName: t(getTranslationKey('averageAcquisitionPrice')),
        size: getColumnSize('averageAcquisitionPriceAssetCurrency'),
        Icon: DollarSign
      }
    );
  } else {
    columns.push({
      prop: 'currentPrice',
      displayName: t(getTranslationKey('currentPrice')),
      size: getColumnSize('currentPrice')
    });
  }

  columns.push(
    {
      prop: 'OneWeek',
      sortProp: 'HistoricalPrices.OneWeek',
      displayName: t(getTranslationKey('OneWeek')),
      size: getColumnSize('OneWeek'),
      Icon: TrendingUpDown
    },
    {
      prop: 'OneMonth',
      sortProp: 'HistoricalPrices.OneMonth',
      displayName: t(getTranslationKey('OneMonth')),
      size: getColumnSize('OneMonth'),
      Icon: TrendingUpDown
    },
    {
      prop: 'ThreeMonths',
      sortProp: 'HistoricalPrices.ThreeMonths',
      displayName: t(getTranslationKey('ThreeMonths')),
      size: getColumnSize('ThreeMonths'),
      Icon: TrendingUpDown
    },
    {
      prop: 'SixMonths',
      sortProp: 'HistoricalPrices.SixMonths',
      displayName: t(getTranslationKey('SixMonths')),
      size: getColumnSize('SixMonths'),
      Icon: TrendingUpDown
    },
    {
      prop: 'ThisYear',
      sortProp: 'HistoricalPrices.ThisYear',
      displayName: t(getTranslationKey('ThisYear')),
      size: getColumnSize('ThisYear'),
      Icon: TrendingUpDown
    },
    {
      prop: 'OneYear',
      sortProp: 'HistoricalPrices.OneYear',
      displayName: t(getTranslationKey('OneYear')),
      size: getColumnSize('OneYear'),
      Icon: TrendingUpDown
    },
    {
      prop: 'ThreeYears',
      sortProp: 'HistoricalPrices.ThreeYears',
      displayName: t(getTranslationKey('ThreeYears')),
      size: getColumnSize('ThreeYears'),
      Icon: TrendingUpDown
    },
    {
      prop: 'FiveYears',
      sortProp: 'HistoricalPrices.FiveYears',
      displayName: t(getTranslationKey('FiveYears')),
      size: getColumnSize('FiveYears'),
      Icon: TrendingUpDown
    },

    {
      prop: 'updated',
      sortProp: 'Updated',
      displayName: t(getTranslationKey('updated')),
      size: getColumnSize('updated')
    }
  );

  return columns;
};

export const useTableColumns = (portfolio: PortfolioBase) => {
  const isPortfolio = isPortfolioCheck(portfolio);
  const defaultColumns = isPortfolio ? DEFAULT_PORTFOLIO_COLUMNS : DEFAULT_WATCHLIST_COLUMNS;
  const selectedColumns = portfolio.settings.tableColumns as TableColumn[];

  return useMemo<{
    selected: PortfolioTableColumn[];
    available: PortfolioTableColumn[];
  }>(() => {
    const columns = createTableColumns(portfolio);

    // Return selected columns in the order of the selectedColumns input array
    const selected = (selectedColumns.length ? selectedColumns : defaultColumns)
      .map(col => columns.find(c => c.prop === col))
      .filter(Boolean) as PortfolioTableColumn[];

    const available = columns.filter(col => !selected.includes(col));

    return {
      selected,
      available
    };
  }, [defaultColumns, portfolio, selectedColumns]);
};

export const useDraggableTableColumns = (
  selectedColumns: TableColumn[],
  portfolio: PortfolioBase
) => {
  return useMemo<{
    selected: DraggablePortfolioTableColumn[];
    available: DraggablePortfolioTableColumn[];
  }>(() => {
    const columns = createTableColumns(portfolio);

    // Return selected columns in the order of the selectedColumns input array
    const selected = selectedColumns
      .map(col => columns.find(c => c.prop === col))
      .filter(Boolean) as PortfolioTableColumn[];
    const available = columns.filter(
      col => !selectedColumns.includes(col.prop) && !NON_SELECTABLE_COLUMNS.includes(col.prop)
    );

    return {
      selected: selected.map(col => ({ type: 'column', ...col, isSelected: true })),
      available: available.map(col => ({ type: 'column', ...col, isSelected: false }))
    };
  }, [portfolio, selectedColumns]);
};

export const getTranslationKey = (column: TableColumn) => {
  switch (column) {
    case 'name':
      return 'AbpAccount::DisplayName:Name';
    case 'currentPrice':
      return '::AssetPrice';
    case 'currentPriceChange':
      return '::Today';
    case 'totalChange':
      return '::TotalGain';
    case 'calculatedValue':
      return '::Value';
    case 'calculatedValueShare':
      return '::Share';
    case 'averageAcquisitionPrice':
      return '::Portfolio:Column:AverageAcquisitionPrice';

    case 'OneWeek':
    case 'OneMonth':
    case 'ThreeMonths':
    case 'SixMonths':
    case 'ThisYear':
    case 'OneYear':
    case 'ThreeYears':
    case 'FiveYears':
      return `::Enum:DateConstant:${DateConstant[column]}`;

    case 'updated':
      return '::Updated';
  }

  return '';
};
